import BackgroundHue from '@atoms/BackgroundHue/BackgroundHue';
import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import GlowColourEnum from '@models/enums/GlowColourEnum';
import type { IImage } from '@models/IImage';
import type { ILink } from '@models/ILink';
import type { IMeta } from '@models/IMeta';
import PulseAnimationWrapper from '@molecules/PulseAnimationWrapper/PulseAnimationWrapper';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';

interface Props {
  readonly backgroundGlow?: GlowColourEnum;
  readonly cta?: ILink;
  readonly desktopImage?: IImage;
  readonly featureText: string;
  readonly isHero?: boolean;
  readonly leftAlignment?: boolean;
  readonly logo?: IImage;
  readonly meta?: IMeta;
  readonly mobileImage?: IImage;
  readonly pulseBackgroundAnimationEnabled: boolean;
  readonly reducedPadding: boolean;
  readonly subText: string;
  readonly warrantyLogo?: IImage;
}

const SectionHeader = ({
  backgroundGlow,
  cta,
  desktopImage,
  featureText,
  isHero,
  leftAlignment,
  logo,
  meta,
  mobileImage,
  pulseBackgroundAnimationEnabled,
  reducedPadding,
  subText,
  warrantyLogo,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const imageToDisplay = isMobile ? mobileImage || desktopImage : desktopImage;
  const imageEditHint =
    isMobile && !!mobileImage
      ? meta?.editHints?.mobileImage
      : meta?.editHints?.desktopImage;
  const TitleTag = isHero ? 'h1' : 'p';

  return (
    <section
      className={classNames(
        'bg-white dark:bg-black-100',
        reducedPadding ? 'pb-10 lg:pb-20' : 'pb-13 lg:pb-[150px]'
      )}
      data-testid="CT-156-Section-Header"
    >
      <SoftLoadWrapper>
        <div
          className={classNames(
            'container flex flex-col items-center justify-center gap-10 overflow-hidden',
            pulseBackgroundAnimationEnabled ? 'xl:grid xl:grid-cols-12' : ''
          )}
        >
          <div
            className={classNames(
              'z-10 flex flex-col gap-7.5 xl:grid xl:grid-cols-12',
              pulseBackgroundAnimationEnabled ? 'xl:col-span-full' : ''
            )}
          >
            {logo && (
              <div
                className={classNames(
                  'flex items-center xl:col-span-6 xl:col-start-4',
                  {
                    'justify-center': !leftAlignment,
                  }
                )}
              >
                <Image
                  image={logo}
                  imageClasses="object-contain w-full h-10"
                  epiEdit={meta?.editHints?.logo as string}
                  dataTestId="CT-156-Section-Header-Logo"
                />
              </div>
            )}

            <TitleTag
              className={classNames(
                'text-black-100 xl:col-span-6 xl:col-start-4 dark:text-white',
                leftAlignment ? 'text-left' : 'text-center',
                {
                  h2: !isHero,
                }
              )}
              data-epi-property-name={meta?.editHints?.featureText}
              data-testid="CT-156-Section-Header-Feature-Text"
            >
              {featureText}
            </TitleTag>

            <p
              className={classNames(
                'body-two xl:col-span-6 xl:col-start-4',
                leftAlignment ? 'text-left' : 'text-center',
                isHero ? 'text-white' : 'text-grey-500'
              )}
              data-epi-property-name={meta?.editHints?.subText}
              data-testid="CT-156-Section-Header-Subtext"
            >
              {subText}
            </p>

            {cta?.url && cta.name && (
              <div
                className={classNames(
                  'flex items-center xl:col-span-6 xl:col-start-4',
                  {
                    'justify-center': !leftAlignment,
                  }
                )}
              >
                <Button
                  link={cta}
                  linkCallback={() => {
                    updateDataLayer({
                      event: 'selectContent',
                      content_type: featureText,
                      link_text: cta.name,
                    });
                  }}
                  editHint={meta?.editHints?.cta as string}
                />
              </div>
            )}
          </div>

          <PulseAnimationWrapper
            containerClasses="xl:col-start-3 xl:col-span-8 min-h-[230px] md:min-h-[388px] lg:min-h-[518px] 2xl:min-h-[648px]"
            imageLogoDimensionClasses="w-[60px] md:w-[100px] lg:w-[140px]"
            logo={warrantyLogo}
            logoPositionClasses="top-[10%] right-[7%] md:right-[10%] 2xl:right-[15%]"
            shouldAnimate={pulseBackgroundAnimationEnabled}
            wrapperClasses="w-full xl:grid xl:col-span-full xl:grid-cols-12"
            videoPlayButtonPositionClassName="bottom-2.5 left-[-33%] md:bottom-5 lg:bottom-[50px] xl:bottom-5 xl:left-[-50%]"
            videoWrapperClasses="max-w-[60%] pb-7.5 md:pb-0"
          >
            {imageToDisplay && (
              <div className="relative flex h-full w-full flex-col items-center justify-center">
                {backgroundGlow !== GlowColourEnum.None &&
                  !pulseBackgroundAnimationEnabled && (
                    <BackgroundHue
                      className={
                        'absolute -top-[9.5rem] scale-y-[0.3] opacity-40 md:-top-72 lg:-top-[25rem] lg:scale-x-[1.5]'
                      }
                      colour={backgroundGlow!}
                    />
                  )}

                <Image
                  image={imageToDisplay}
                  imageClasses={classNames('w-full h-auto')}
                  classes="z-10 w-full"
                  epiEdit={imageEditHint as string}
                />
              </div>
            )}
          </PulseAnimationWrapper>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default SectionHeader;
